import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3';
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  memo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import { ProjectAvatar } from '@components/Avatar/ProjectAvatar';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { BaseButtonV3 } from '@components/TaskList/BaseButtonV3';
import { PopupTaskFilterDuedateV3 } from '@components/TaskList/PopupTaskFilterDuedateV3.web';
import { PrioritySelector } from '@components/TaskList/PrioritySelector.web';
import { SimpleHoverable } from '@components/TaskList/SimpleHoverable';
import { TaskAssigneeMembersButtonV3 } from '@components/TaskList/TaskAssigneeMembersButtonV3.web';
import { TaskCreatedToast } from '@components/TaskList/TaskCreatedToast.web';
import {
  UserSearchType,
  StringSearchType,
} from '@components/TaskList/TaskInviteOrAssignee.web';
import { FormValues } from '@components/Tasks/TaskFormStack';
import { EnhancedAnimatedCheckbox } from '@components/Web/AnimatedCheckbox';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  Task,
  TaskStatus,
  TaskPriority,
  useUpdateTaskMutation,
  TaskMemberRole,
  ListProjectsDocument,
} from '@graphql/generated';
import { useLatestRef } from '@hooks/useLatestRef';
import useMe from '@hooks/useMe';
import { useProjectTeamSubscription } from '@hooks/useProjectTeamSubscription';
import { getTaskMembership } from '@hooks/useTaskMembership';
import documentIcons from '@themes/documentIcons';
import Images from '@themes/images';
import theme, { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';
import {
  localDateFromUTCDate,
  beginningOfDayUTC,
} from '@utils/formatters/date';
import { AsyncStorage } from '@utils/storage';
import { shouldShowPaywallForProject } from '@utils/teams/paywall';

import { TaskShareLink } from './TaskShareLink.web';

interface TaskListItemProps {
  item: Task;
  navigation: any;
  onPress?: (task: Task) => void;
  isSelected?: boolean;
  draggable?: boolean;
  toggleCompleteTask: (task: Task) => void;
  hasBorder?: boolean;
  focusOutside?: boolean;
  hideProject?: boolean;
}

export const getFirstLine = (text: string) => {
  if (!text) return '';
  const match = /^([^\r\n]*)/.exec(text);
  return match ? match[0] : text;
};

export const isMultiLine = (text: string) => {
  if (!text) return false;
  return /[\r\n]/.test(text.trim());
};

const TASK_NAME_MAX_VISIBLE_CHARS = 24;
const TASK_NAME_ELLIPSIS = '...';

export const truncateTaskNameForWeb = (taskName: string) => {
  const MAX_TEXT_CHARS_WITH_ELLIPSIS =
    TASK_NAME_MAX_VISIBLE_CHARS - TASK_NAME_ELLIPSIS.length;
  const multiLine = isMultiLine(taskName);
  const singleLine = multiLine ? getFirstLine(taskName) : taskName;

  if (singleLine.length > TASK_NAME_MAX_VISIBLE_CHARS || multiLine) {
    return (
      singleLine.slice(0, MAX_TEXT_CHARS_WITH_ELLIPSIS) + TASK_NAME_ELLIPSIS
    );
  }

  return singleLine;
};

function isTruncatedSingleLine(el: HTMLElement | null) {
  if (!el) return false;
  return el.scrollWidth > el.clientWidth;
}

function isTruncatedMultiLine(el: HTMLElement | null) {
  if (!el) return false;
  return el.scrollHeight > el.clientHeight;
}

/**
 * Observe element resize and re-measure truncation.
 * This prevents re-measuring on every render.
 */
function useTruncationObserver(params: {
  projectName?: string | null;
  taskName?: string | null;
  projectElRef: React.RefObject<HTMLElement | null>;
  taskElRef: React.RefObject<HTMLElement | null>;
}) {
  const { projectName, taskName, projectElRef, taskElRef } = params;

  const [isProjectNameTruncated, setIsProjectNameTruncated] = useState(false);
  const [isTaskNameDomTruncated, setIsTaskNameDomTruncated] = useState(false);

  // Measure when text changes (cheap, runs once per name change).
  useEffect(() => {
    setIsProjectNameTruncated(isTruncatedSingleLine(projectElRef.current));
  }, [projectName, projectElRef]);

  useEffect(() => {
    setIsTaskNameDomTruncated(isTruncatedMultiLine(taskElRef.current));
  }, [taskName, taskElRef]);

  // Measure again when element size changes.
  useEffect(() => {
    const projectEl = projectElRef.current;
    const taskEl = taskElRef.current;

    if (typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(() => {
      setIsProjectNameTruncated(isTruncatedSingleLine(projectElRef.current));
      setIsTaskNameDomTruncated(isTruncatedMultiLine(taskElRef.current));
    });

    if (projectEl) ro.observe(projectEl);
    if (taskEl) ro.observe(taskEl);

    return () => ro.disconnect();
  }, [projectElRef, taskElRef]);

  return { isProjectNameTruncated, isTaskNameDomTruncated };
}

/** Memoized checkbox UI (isolated to reduce parent re-render impact). */
const CompleteCell = memo(function CompleteCell(props: {
  canEdit: boolean;
  completedAt: string | null | undefined;
  item: Task;
  toggleCompleteTask: (task: Task) => void;
}) {
  const { canEdit, completedAt, item, toggleCompleteTask } = props;

  if (canEdit) {
    return (
      <SimpleHoverable style={{ flex: 1, cursor: 'pointer' }}>
        {(h) => (
          <Box
            flexDirection='row'
            alignItems='center'
            justifyContent='flex-start'
            // Checkbox toggles task completion — don't also open the
            // task modal when the outer row-click handler fires.
            data-ignore-row-click>
            <EnhancedAnimatedCheckbox
              accessibilityLabel={
                completedAt
                  ? `Mark incomplete | ${item.name}`
                  : `Mark complete | ${item.name}`
              }
              onValueChange={() => {
                toggleCompleteTask(item);
              }}
              isRadioButton={true}
              onHoverChange={() => {}}
              isHovered={h}
              isChecked={!!completedAt}
            />
          </Box>
        )}
      </SimpleHoverable>
    );
  }

  if (completedAt) {
    return <Icon name='StateYes' variant='s' color='grey04' />;
  }

  return (
    <RadioButton
      disabled={true}
      onPress={() => {}}
      isSelected={!!completedAt}
      iconVariant='s'
    />
  );
});

/**
 * Heavy “name + project + checklist” block extracted into memo component.
 * Only re-renders when props used by UI actually change.
 */
export const NameProjectBlock = memo(
  function NameProjectBlock(props: {
    item: Task;
    navigation: any;
    onPress?: (task: Task) => void;
    completeNode: React.ReactNode;
    isTaskNameTruncated: boolean;
    isProjectNameTruncated: boolean;
    activeChecklistCount: number;
    taskNameRef: React.RefObject<HTMLElement | null>;
    projectNameRef: React.RefObject<HTMLElement | null>;
    hideProject?: boolean;
  }) {
    const {
      item,
      navigation,
      onPress,
      completeNode,
      isTaskNameTruncated,
      isProjectNameTruncated,
      activeChecklistCount,
      taskNameRef,
      projectNameRef,
      hideProject,
    } = props;

    const [isTextHovered, setIsTextHovered] = useState(false);

    const { t } = useTranslation('models');
    return (
      <Box
        flex={1}
        mr='m'
        accessibilityLabel={item.name}
        flexDirection='row'
        alignItems='center'>
        <Box justifyContent='center' marginRight='m'>
          {completeNode}
        </Box>

        <Box flex={1} maxWidth={300}>
          <TooltipWebV3
            marginOffset={0}
            disableHoverListener={!isTaskNameTruncated}
            disableFocusListener={!isTaskNameTruncated}
            disableTouchListener={!isTaskNameTruncated}
            title={
              <Text
                numberOfLines={0}
                maxWidth={280}
                flexShrink={1}
                variant='labelSmall'
                color='white'>
                {item.name}
              </Text>
            }
            placement='bottom-start'>
            <div style={{ flex: 1 }}>
              <Box flex={1} flexDirection='row' alignItems='center'>
                {/*
                 * Task-name click is handled by the outer row's onClick
                 * handler on this web component (the whole row is the
                 * touch target). Leaving onPress on the Text too would
                 * fire `onPress(item)` twice per click — once from the
                 * Text's RN Web onClick-bound onPress, once from the
                 * row's bubbling onClick — and open the task modal
                 * twice via the 500ms deferred onPress in MyTaskList.
                 */}
                <Text
                  ref={taskNameRef}
                  textDecorationLine={isTextHovered ? 'underline' : 'none'}
                  flexShrink={1}
                  numberOfLines={2}
                  accessibilityLabel='Task'
                  variant='webSmall'
                  color='textPrimary'
                  onMouseEnter={() => setIsTextHovered(true)}
                  onMouseLeave={() => setIsTextHovered(false)}>
                  {item.name}
                </Text>
              </Box>
            </div>
          </TooltipWebV3>

          {(!hideProject || activeChecklistCount > 0) && (
            <Box
              testID='task-row-secondary-line'
              flexShrink={1}
              flexDirection='row'
              alignItems='center'
              marginTop='xxs'>
              {!hideProject && (
                <TouchableOpacity
                  accessibilityLabel={`Project: ${
                    item.project?.name || 'No Project'
                  }`}
                  onPress={() =>
                    item.project && navigation.navigateToProject(item.project)
                  }
                  // Clicking the project name navigates to the project —
                  // scope the row-click opt-out to just this interactive
                  // touchable so the surrounding whitespace still opens
                  // the task as the row-level handler intends.
                  data-ignore-row-click
                  style={{ flexShrink: 1 }}>
                  <TooltipWebV3
                    marginOffset={0}
                    disableHoverListener={!isProjectNameTruncated}
                    disableFocusListener={!isProjectNameTruncated}
                    disableTouchListener={!isProjectNameTruncated}
                    title={
                      <Text
                        numberOfLines={0}
                        maxWidth={300}
                        flexShrink={1}
                        variant='labelSmall'
                        color='white'>
                        {item.project?.name ?? 'No Project Selected'}
                      </Text>
                    }
                    placement='bottom-start'>
                    <div style={{ flex: 1 }}>
                      <Box flex={1} flexDirection='row' alignItems='center'>
                        <ProjectAvatar
                          icon={item?.project?.icon}
                          color={item?.project?.color}
                        />
                        <Text
                          ref={projectNameRef}
                          flexShrink={1}
                          ml='xxs'
                          mr='xs'
                          ellipsizeMode='tail'
                          accessibilityLabel='Task'
                          variant='metadataSecondary'
                          color='grey04'
                          style={{ letterSpacing: 0.02 }}
                          numberOfLines={1}>
                          {item.project?.name || 'Unknown Project'}
                        </Text>
                      </Box>
                    </div>
                  </TooltipWebV3>
                </TouchableOpacity>
              )}

              {activeChecklistCount > 0 && (
                <TouchableOpacity
                  onPress={() => {
                    AsyncStorage.setItem('TaskWebPanelOpenChecklist', '1');
                    onPress?.(item);
                  }}
                  // Inner handler already opens the task; without this the
                  // bubbling click would fire the row-level onPress too and
                  // open the task twice.
                  data-ignore-row-click>
                  <Box flexDirection='row' alignItems='center'>
                    <Icon marginLeft='xxxs' name='Checklist' variant='xxs' />
                    <Text
                      flexShrink={1}
                      ml='xxs'
                      ellipsizeMode='tail'
                      accessibilityLabel='Task Checklist Count'
                      variant='labelRequired'
                      color='textSecondary'
                      textDecorationLine='underline'
                      textDecorationColor='textSecondary'
                      style={{ letterSpacing: 0.02 }}
                      numberOfLines={1}>
                      {t('tasks.checklist.checklistRemainingWithCount', {
                        count: activeChecklistCount,
                      })}
                    </Text>
                  </Box>
                </TouchableOpacity>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  },
  (prev, next) => {
    // Custom compare: only re-render when UI-relevant values change.
    return (
      prev.item.id === next.item.id &&
      prev.item.name === next.item.name &&
      prev.item.completedAt === next.item.completedAt &&
      prev.item.status === next.item.status &&
      prev.item.priority === next.item.priority &&
      prev.item.project?.id === next.item.project?.id &&
      prev.item.project?.name === next.item.project?.name &&
      prev.item.project?.icon === next.item.project?.icon &&
      prev.item.project?.color === next.item.project?.color &&
      prev.activeChecklistCount === next.activeChecklistCount &&
      prev.isTaskNameTruncated === next.isTaskNameTruncated &&
      prev.isProjectNameTruncated === next.isProjectNameTruncated &&
      prev.completeNode === next.completeNode && // reference stable if parent memoizes it
      prev.onPress === next.onPress &&
      prev.navigation === next.navigation &&
      prev.hideProject === next.hideProject
    );
  }
);

const PrioritySelectorPopup: React.FC<{
  onClose: () => void;
  onPrioritySelect: (p: TaskPriority) => void;
}> = ({ onClose, onPrioritySelect }) => (
  <PrioritySelector
    onPrioritySelect={(p) => {
      onPrioritySelect(p);
      onClose();
    }}
  />
);

const DueDatePopup: React.FC<{
  onClose: () => void;
  selectedDate: Date | undefined;
  onSelectDate: (d: Date | undefined) => void;
}> = ({ onClose, selectedDate, onSelectDate }) => (
  <PopupTaskFilterDuedateV3
    onClose={() => {}}
    onSelectDate={(date) => {
      onSelectDate(date);
      setTimeout(onClose, 500);
    }}
    selectedDate={selectedDate}
  />
);

export const TaskListItemWeb: React.FC<TaskListItemProps> = memo(
  ({
    item,
    navigation,
    onPress,
    toggleCompleteTask,
    hasBorder = true,
    isSelected = false,
    draggable = false,
    focusOutside = false,
    hideProject = false,
  }) => {
    const toast = useToast();
    const { me } = useMe();
    const { setPaywallModalOpen, setPaywallTeam } = useWebDrawer();

    // Keep latest item for callbacks without changing dependencies.
    const itemRef = useLatestRef(item);

    const [hover, setHover] = useState(false);
    // Pop modal states
    const [showInviteOrAssignModal, setShowInviteOrAssignModal] =
      useState(false);
    const [showPriorityModal, setShowPriorityModal] = useState(false);
    const [showDueDateSelectorModal, setShowDueDateSelectorModal] =
      useState(false);

    // Sync local due date with server changes
    const [currentDueDate, setCurrentDueDate] = useState<Date | undefined>(
      item.dueDate ? localDateFromUTCDate(item.dueDate) : undefined
    );
    useEffect(() => {
      setCurrentDueDate(
        item.dueDate ? localDateFromUTCDate(item.dueDate) : undefined
      );
    }, [item.dueDate]);

    const getTaskCategory = (task: Task): string => {
      if (task.status === TaskStatus.Completed || task.completedAt) {
        return 'Completed';
      }
      if (task.dueDate) {
        const dueDate = new Date(task.dueDate);
        const now = new Date();
        now.setHours(0, 0, 0, 0); // Start of today
        if (dueDate < now) {
          return 'Overdue';
        }
      }
      return 'Current';
    };

    const category = getTaskCategory(item);
    const taskItemAccessibilityLabel = `Task Item | ${category} | ${
      item.project?.name || 'No Project'
    } | ${getFirstLine(item.name)}`;

    const showToast = useCallback(
      (
        onPressToast: () => void,
        onCloseToast: () => void,
        title: string | undefined,
        msg: string | undefined,
        iconName?: keyof typeof Images | keyof typeof documentIcons
      ) => {
        return toast.show(
          <TaskCreatedToast
            title={title ?? 'Task Completed'}
            iconName={iconName}
            message={msg}
            duration={3000}
            onPress={onPressToast}
          />,
          {
            type: 'custom',
            duration: 3000,
            placement: 'bottom',
            animationType: 'slide-in',
            style: {
              marginBottom: 39,
              backgroundColor: 'transparent',
              shadowColor: 'transparent',
            },
            onClose: () => {
              onCloseToast();
            },
          }
        );
      },
      [toast]
    );

    // Helper function to determine task category for accessibility

    const [updateTask] = useUpdateTaskMutation({
      refetchQueries: [
        { query: ListProjectsDocument, variables: { includeRecent: false } },
        'listTasks',
        'listProjects',
      ],
    });

    // TA-10958: listTasks rows carry no team billing; the paywall checks
    // below and the paywall-aware children read it from the team instead.
    // While it is unknown the assign and share-link controls stay disabled:
    // a check on missing billing answers "no paywall" (fail open).
    const { project: paywallProject, billingPending } =
      useProjectTeamSubscription(item.project);

    // Permission checks
    const { canEditTaskThirdLevel } = useMemo(
      () => getTaskMembership(item, me?.id),
      [item, me?.id]
    );

    const completed = useMemo(() => {
      const { status, completedAt } = item;
      return status === TaskStatus.Completed || !!completedAt;
    }, [item.status, item.completedAt]);

    // Checklist count memo
    const activeChecklistCount = useMemo(
      () => item.checklists?.filter((i) => i.completedAt === null)?.length ?? 0,
      [item.checklists]
    );

    // Precompute selected users (avoid map/filter every render)
    const selectedUsers = useMemo(() => {
      const list = item.members
        .filter((t) => t.role !== TaskMemberRole.Owner)
        .map((t) => {
          return {
            ...t.user,
            role: t.role,
            isTaskTagUser: false,
          } as UserSearchType;
        });
      const ttUserlist =
        item.userRequests?.map((t) => {
          return {
            ...t.user,
            role: t.role,
            isTaskTagUser: true,
          } as UserSearchType;
        }) ?? [];
      const emailList = (item.invitations ?? []).map(
        (t) =>
          ({
            title: t.email,
            role: t.role,
            expiredAt: t.expiredAt,
            id: t.id,
            __typename: 'StringSearchType',
          }) as StringSearchType
      );
      return [...list, ...ttUserlist, ...emailList];
    }, [item.members, item.invitations, item.userRequests]);

    // Memoize complete node
    const completeNode = useMemo(() => {
      return (
        <CompleteCell
          canEdit={canEditTaskThirdLevel}
          completed={completed}
          completedAt={item.completedAt}
          item={item}
          toggleCompleteTask={toggleCompleteTask}
        />
      );
    }, [
      canEditTaskThirdLevel,
      completed,
      item,
      item.completedAt,
      toggleCompleteTask,
    ]);

    // Form values memo (only changes when relevant fields change)
    const initialValues: FormValues = useMemo(() => {
      const startDate = item.startDate && localDateFromUTCDate(item.startDate);
      const dueDate = item.dueDate && localDateFromUTCDate(item.dueDate);

      return {
        name: item.name ?? '',
        description: item.description ?? '',
        fixedOrder: item?.fixedOrder ?? false,
        frequency: item?.frequency ?? undefined,
        dependencyIds: item.dependencies?.map((d) => d.id) || [],
        recurring: item?.recurring ?? false,
        projectId: item?.projectId ?? '0',
        phaseId: item?.phaseId ?? undefined,
        priority: item?.priority ?? TaskPriority.Low,
        status: item?.status ?? TaskStatus.Ongoing,
        members: (item?.members ?? []).map((m) => ({
          userId: m.user.id,
          role: m.role,
        })),
        skillIds: item?.skillIds ?? [],
        startDate,
        dueDate,
        completedAt: item?.completedAt ?? null,
      };
    }, [
      item.id,
      item.name,
      item.description,
      item.fixedOrder,
      item.frequency,
      item.recurring,
      item.projectId,
      item.phaseId,
      item.priority,
      item.status,
      item.completedAt,
      item.startDate,
      item.dueDate,
      item.dependencies,
      item.members,
      item.skillIds,
    ]);

    // Update priority callback
    const doUpdatePriority = useCallback(
      (priority: TaskPriority) => {
        if (priority === item.priority) return;

        const afterUpdate = () => {
          const toastId = showToast(
            () => setTimeout(() => toast.hide(toastId), 0),
            () => {},
            'Priority updated.',
            undefined,
            undefined
          );
        };

        updateTask({
          variables: {
            id: item.id,
            attributes: { ...initialValues, priority },
          },
        }).then(afterUpdate);
      },
      [updateTask, item.id, item.priority, initialValues, showToast, toast]
    );

    const PriorityModalComponent = useCallback(
      ({ onClose }: { onClose: () => void }) => (
        <PrioritySelectorPopup
          onClose={onClose}
          onPrioritySelect={doUpdatePriority}
        />
      ),
      [doUpdatePriority]
    );

    const DueDateModalComponent = useCallback(
      ({ onClose }: { onClose: () => void }) => (
        <DueDatePopup
          onClose={onClose}
          selectedDate={currentDueDate}
          onSelectDate={setCurrentDueDate}
        />
      ),
      [currentDueDate]
    );

    // Priority button memo
    const priorityNode = useMemo(() => {
      return (
        <BaseButtonV3
          disabled={!canEditTaskThirdLevel}
          mr='s'
          modalRadius={8}
          onModalShow={(visible) => {
            setShowPriorityModal(visible);
          }}
          popModalComponent={PriorityModalComponent}>
          {item.priority === TaskPriority.High && (
            <Icon name='ChevronsUp' color='alertRed' variant='s' />
          )}
          {item.priority === TaskPriority.Low && (
            <Icon name='ChevronsDown' color='blue' variant='s' />
          )}
          {item.priority === TaskPriority.Medium && (
            <Icon name='Equals' color='pastelYellow' variant='s' />
          )}
        </BaseButtonV3>
      );
    }, [canEditTaskThirdLevel, PriorityModalComponent, item.priority]);

    const { t: f } = useTranslation('format');
    const dueDateNode = useMemo(() => {
      return (
        <BaseButtonV3
          disabled={!canEditTaskThirdLevel}
          mr='s'
          onModalShow={(visible) => {
            setShowDueDateSelectorModal(visible);
          }}
          popModalComponent={DueDateModalComponent}>
          <Box
            width={123}
            height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
            flexDirection='row'
            borderColor={canEditTaskThirdLevel ? 'grey02' : 'grey04'}
            alignItems='center'
            justifyContent='center'>
            <Icon
              variant='xxs'
              name='Calendar'
              color={
                !canEditTaskThirdLevel && !currentDueDate
                  ? 'grey04'
                  : 'textSecondary'
              }
            />
            <Text
              marginLeft='xxs'
              variant='webBodyHintSubline'
              color={
                !canEditTaskThirdLevel && !currentDueDate
                  ? 'grey04'
                  : 'textSecondary'
              }>
              {currentDueDate
                ? f('monthAndDayYearV3', {
                    val: localDateFromUTCDate(currentDueDate),
                  })
                : 'Due Date'}
            </Text>
          </Box>
        </BaseButtonV3>
      );
    }, [canEditTaskThirdLevel, DueDateModalComponent, item.dueDate]);

    // Truncation observer: avoids measuring every render
    const textTaskNameRef = useRef<HTMLElement | null>(null);
    const textProjectNameRef = useRef<HTMLElement | null>(null);
    const { isProjectNameTruncated, isTaskNameDomTruncated } =
      useTruncationObserver({
        projectName: item.project?.name,
        taskName: item.name,
        projectElRef: textProjectNameRef,
        taskElRef: textTaskNameRef,
      });

    const isTaskNameTruncated =
      isMultiLine(item.name) ||
      item.name.length > TASK_NAME_MAX_VISIBLE_CHARS ||
      isTaskNameDomTruncated;

    // Background color computed, not function
    const backgroundColor = useMemo(() => {
      if (focusOutside) {
        return 'white';
      }
      if (
        isSelected ||
        showInviteOrAssignModal ||
        showPriorityModal ||
        showDueDateSelectorModal
      ) {
        return theme.colors.greenSecondaryMild;
      }
      // Task-row hover (TA-9005): grey01; selected keeps greenSecondaryMild.
      // The row stays tinted while hovering controls inside it — those
      // controls deepen to grey02 instead (e.g. TaskShareLink below).
      if (hover) return theme.colors.grey01;
      return 'white';
    }, [
      isSelected,
      showInviteOrAssignModal,
      showPriorityModal,
      showDueDateSelectorModal,
      hover,
      focusOutside,
    ]);

    // Due date dismiss handler (avoid re-creating inline)
    const onDismissDueDate = useCallback(() => {
      const original = itemRef.current.dueDate
        ? localDateFromUTCDate(itemRef.current.dueDate)
        : undefined;

      const originalTs = original ? original.getTime() : null;
      const currentTs = currentDueDate ? currentDueDate.getTime() : null;

      if (currentTs !== originalTs && currentDueDate) {
        updateTask({
          variables: {
            id: itemRef.current.id,
            attributes: {
              ...initialValues,
              dueDate: beginningOfDayUTC(currentDueDate).toISOString(),
            },
          },
        });
      }

      setShowDueDateSelectorModal(false);
    }, [currentDueDate, updateTask, initialValues, itemRef]);
    useEffect(() => {
      onDismissDueDate();
    }, [currentDueDate]);

    const showSortIcon = !!draggable && (hover || isSelected);

    // The whole row visually reads as clickable (cursor:pointer across
    // the entire 56px-tall surface), but without a row-level handler
    // only the inner <Text accessibilityLabel='Task'> captured the
    // press — clicking the row's empty white space between the task
    // name and the assignee button did nothing, a surprising UX dead
    // zone.
    //
    // Wire onPress to the outer row. Child interactive elements —
    // project link, assignee button, share link — opt out individually
    // with `data-ignore-row-click` (scoped to each touchable, not the
    // surrounding layout Box, so whitespace between them still opens
    // the task). Keyboard activation via Enter/Space mirrors what a
    // <button> would do for a11y.
    const handleRowClick = (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      if (target && target.closest('[data-ignore-row-click]')) return;
      // Shift/Ctrl/Cmd-click is a multi-select gesture — SelectableDndKitList.web's
      // mousedown-capture handler already updated the range/toggle selection
      // by the time this bubbling click fires. Opening the task panel here
      // too would hijack every multi-select click into a navigation.
      if (event.shiftKey || event.ctrlKey || event.metaKey) return;
      onPress?.(item);
    };
    const handleRowKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const target = event.target as HTMLElement | null;
      if (target && target.closest('[data-ignore-row-click]')) return;
      event.preventDefault();
      onPress?.(item);
    };
    return (
      <div
        role='button'
        tabIndex={0}
        aria-label={taskItemAccessibilityLabel}
        onClick={handleRowClick}
        onKeyDown={handleRowKeyDown}
        style={{ display: 'flex', flex: 1 }}>
        <Box
          accessibilityLabel={taskItemAccessibilityLabel}
          onMouseEnter={() => {
            setHover(true);
          }}
          onMouseLeave={() => {
            setHover(false);
          }}
          style={{
            flex: 1,
            cursor: 'pointer',
            minHeight: 56,
            borderBottomRightRadius: hasBorder ? 0 : 8,
            borderBottomLeftRadius: hasBorder ? 0 : 8,
            borderBottomWidth: hasBorder ? 1 : 0,
            borderBottomColor: hasBorder ? theme.colors.grey02 : 'none',
            backgroundColor: backgroundColor,
          }}>
          <Box
            flex={1}
            flexDirection='row'
            paddingHorizontal='s'
            alignItems='center'>
            <Box
              width={20}
              height={20}
              alignItems='center'
              justifyContent='center'>
              {showSortIcon && <Images.MoveSmall />}
            </Box>

            <Box
              minWidth={200}
              justifyContent='center'
              flex={1}
              paddingVertical='xs'
              alignContent='center'>
              <NameProjectBlock
                item={item}
                navigation={navigation}
                onPress={onPress}
                completeNode={completeNode}
                isTaskNameTruncated={isTaskNameTruncated}
                isProjectNameTruncated={isProjectNameTruncated}
                activeChecklistCount={activeChecklistCount}
                taskNameRef={textTaskNameRef}
                projectNameRef={textProjectNameRef}
                hideProject={hideProject}
              />
            </Box>

            {/*
             * Priority, due-date, and assignee each trigger their own
             * action — opt out of the row click individually. Marking the
             * shared layout wrapper instead would also opt out the
             * whitespace between controls and reintroduce a dead zone.
             */}
            <Box flexDirection='row' alignItems='center'>
              <Box data-ignore-row-click>{priorityNode}</Box>

              <Box data-ignore-row-click>{dueDateNode}</Box>

              <Box
                data-ignore-row-click
                onClick={(e: any) => e.stopPropagation()}>
                <TaskAssigneeMembersButtonV3
                  accessibilityLabel='Task assignees'
                  doNotShrink={true}
                  disabled={!canEditTaskThirdLevel || billingPending}
                  selectedUser={selectedUsers}
                  onAssignMember={() => {
                    // Check paywall using project.team.subscriptionDetails and project.currentUserTeamRole (no API call)
                    if (
                      paywallProject &&
                      shouldShowPaywallForProject(paywallProject)
                    ) {
                      setPaywallTeam(paywallProject?.team ?? undefined);
                      setPaywallModalOpen(true);
                      return;
                    }
                  }}
                  onSetShowInviteOrAssignModal={(show) => {
                    // Check paywall using project.team.subscriptionDetails and project.currentUserTeamRole (no API call)
                    if (
                      show &&
                      paywallProject &&
                      shouldShowPaywallForProject(paywallProject)
                    ) {
                      setPaywallTeam(paywallProject?.team ?? undefined);
                      setPaywallModalOpen(true);
                      return;
                    }
                    setShowInviteOrAssignModal(show);
                  }}
                  taskId={item.id}
                  project={paywallProject}
                />
              </Box>
            </Box>
            <Box
              height={32}
              width={32}
              borderRadius='xs'
              ml='xxs'
              mr='s'
              alignItems='center'
              justifyContent='center'
              data-ignore-row-hover
              data-ignore-row-click>
              <TaskShareLink
                taskId={item.id}
                project={paywallProject}
                canEdit={!billingPending}
                noMarginRight={true}
                // Row hover is grey01 — the icon deepens one step; on any
                // other row surface (selected/white) it tints grey01.
                hoverBackgroundColor={
                  backgroundColor === theme.colors.grey01 ? 'grey02' : 'grey01'
                }
              />
            </Box>
          </Box>
        </Box>
      </div>
    );
  },
  (prev, next) => {
    // Parent-level memo compare to avoid re-render when parent list re-renders with same item snapshot.
    // Include item reference so Apollo cache updates (e.g. subscriptionDetails changes) propagate to
    // paywall-aware children (TaskAssigneeMembersButtonV3, TaskShareLink).
    return (
      prev.item === next.item &&
      prev.isSelected === next.isSelected &&
      prev.draggable === next.draggable &&
      prev.hasBorder === next.hasBorder &&
      prev.onPress === next.onPress &&
      prev.toggleCompleteTask === next.toggleCompleteTask &&
      prev.hideProject === next.hideProject
    );
  }
);

// Add display name for better debugging
TaskListItemWeb.displayName = 'TaskListItem';
